import React, { useRef, useEffect } from 'react';
import { TextField, Text, ShimmerElementType, Separator, Shimmer, Link, TooltipHost, Image } from '@fluentui/react';
import './SearchCustomer.scss';
import { useId } from '@fluentui/react-hooks';
import InfoIcon from '../../../../../app/static/icons/tooltip-info-icon.svg';
import { useGetCustomersList } from 'app/services/search-api';
import { Trans } from 'react-i18next';
import { keyPress } from 'app/components/helpers';
import { MsxSuggestDocument, MsxSuggestResponse } from 'generated/search-backend';
import { TelemetryConstants, appInsightsService } from '../../../../utils/constants';

interface IProps {
    status: string;
    setStatus: (status: string) => void;
    toggleSearch: boolean;
    setToggleSearch: (toggle: boolean) => void;
    setCustomerInputType?: (input: string) => void;
    searchValue: string;
    setSearchValue: (value: string) => void;
    setSuggestionSelected: (selected: boolean) => void;
    setFilteredData: (data: MsxSuggestDocument[]) => void;
    setRevenueRisk: (newRisk: string) => void;
    setImpDevices: (newDevices: string) => void;
    setRevenueHText: (hText: boolean) => void;
    setImpDevicesHText: (hText: boolean) => void;
    autoSuggestHText: boolean;
    setAutoSuggestHText: (hText: boolean) => void;
    origin: string;
    showModal?: () => void;
    isFocus?: boolean;
    custName: string;
    setCustName: (name: string) => void;
    customerSelected?: boolean;
    setCustomerSelected?: (selected: boolean) => void;
    lastSelectedCustomerName?: string;
    setLastSelectedCustomerName?: (name: string) => void;
    originalSelected?: MsxSuggestDocument[]; // Currently set as any - track down what this is actually setting
    setOriginalSelected?: (selected: MsxSuggestDocument[]) => void; // Currently set as any - track down what this is actually setting
    searchNotFound?: boolean;
    setSearchNotFound?: (notFound: boolean) => void;
    disabled?: boolean;
    setDisabled?: (disabled: boolean) => void;
    custNameHText?: boolean;
    setCustNameHText?: (hText: boolean) => void;
    setCountry?: (country: string) => void;
}
export const SearchCustomer: React.FC<IProps> = ({
    toggleSearch,
    setToggleSearch,
    setStatus,
    status,
    setCustomerInputType,
    setCountry,
    setSearchNotFound,
    searchValue,
    setSearchValue,
    showModal,
    setSuggestionSelected,
    setFilteredData,
    setRevenueRisk,
    custName,
    setCustName,
    setImpDevices,
    setRevenueHText,
    setImpDevicesHText,
    autoSuggestHText,
    customerSelected,
    setAutoSuggestHText,
    custNameHText,
    setCustNameHText,
    origin,
    isFocus,
    setCustomerSelected,
    setLastSelectedCustomerName,
    originalSelected,
    setOriginalSelected
}) => {
    const [showSuggestion, setShowSuggestion] = React.useState(false);
    const tooltipId = useId('tooltip');
    const calloutProps = { gapSpace: 0 };
    const { data, mutate: getCustomersList, isLoading } = useGetCustomersList();

    const handleSearch = (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (origin === 'ADD_CUSTOMER') {
            setCustomerSelected(false);
            setOriginalSelected([]);
            setFilteredData([]);
        }
        setCustomerInputType('search');
        setSearchValue((e.target as HTMLInputElement | HTMLTextAreaElement).value);
        setAutoSuggestHText(false);
        if (customerSelected) {
            setRevenueHText(false);
            setImpDevicesHText(false);
            setRevenueRisk('');
            setCustName(custName);
            setImpDevices('');
            setSuggestionSelected(false);
        }
        if ((e.target as HTMLInputElement | HTMLTextAreaElement).value.length > 2) {
            setShowSuggestion(true);
            setAutoSuggestHText(false);
            getCustomersList({searchValue: (e.target as HTMLInputElement | HTMLTextAreaElement).value});
        }
        if (!customerSelected && !isFocus) {
            setCustName('');
        } else {
            setShowSuggestion(false);
        }
    };

    const handleSelectSuggestion = (selectedData: MsxSuggestDocument) => {
        if (origin === 'ADD_CUSTOMER') {
            setLastSelectedCustomerName(selectedData.accountName);
            setCustomerSelected(true);
            // Should check the purpose behind originalSelectedData here. Do we want to keep all selected customers and add our new one to this list?
            // Currently, I'm not seeing anywhere we're actually using originalSelected between NewReqExperience, NewCustomer, or SearchCustomer, but will leave this in for now in case we want to add some level of functionality in later.
            setOriginalSelected([...originalSelected, selectedData]);
        }
        setRevenueHText(false);
        setImpDevicesHText(false);
        setFilteredData([selectedData]);
        setSuggestionSelected(true);
        setShowSuggestion(false);
        setSearchValue(selectedData.accountName.toString() + ' - ' + selectedData.accountNumber.toString());
        setSearchNotFound(false);
        setAutoSuggestHText(false);
        setCustName(selectedData.accountName);
        setCustNameHText(false);
        setCountry(selectedData.country);
        setShowSuggestion(false);
        appInsightsService.logRequestflowTelemetry(TelemetryConstants.CUSTOMER_SEARCH_RESULT_SELECTED, 'add_customer',
            {
                'number_of_results': data?.length.toString() || '0',
                'customer_country_iso': selectedData.region || ''
            });
    };

    const handleFocus = () => {
        if (searchValue?.length > 2) {
            getCustomersList({searchValue: searchValue});
            setShowSuggestion(true);
        }
    };

    const suggestionRef = useRef<HTMLDivElement>(null);

    useSuggestionBoxDismiss(suggestionRef, setShowSuggestion);

    //loader shimmer element
    const shimmerElement = [
        { type: ShimmerElementType.line },
        { type: ShimmerElementType.gap, width: '5%' },
        { type: ShimmerElementType.line },
        { type: ShimmerElementType.gap, width: '5%' },
        { type: ShimmerElementType.line }
    ];

    const textInput = useRef(null);

    const filterHelper = (data: MsxSuggestResponse[]) => {
        let uniqueData: MsxSuggestDocument[] = [];
        let accountNumberSet: Set<string> = new Set<string>();
        for (let i = 0; i < data?.length; i++) {
            if (!accountNumberSet.has(data[i].document.accountNumber)) {
                uniqueData.push(data[i].document);
                accountNumberSet.add(data[i].document.accountNumber);
            }
        }

        return uniqueData;
    };

    const handleAddCustomerModal = () => {
        setCustomerInputType('textfield');
        showModal();
        setSearchValue(searchValue);
        setCustName(searchValue);
        appInsightsService.logRequestflowTelemetry(TelemetryConstants.ADD_CUSTOMER_CLICKED, 'add_customer');
    };

    useEffect(() => {
        if (isFocus) {
            textInput.current.focus();
        }
    }, [isFocus, showSuggestion, toggleSearch]);

    useEffect(() => {
        if (data?.length === 0) {
            setSearchNotFound(true);
        } else {
            setSearchNotFound(false);
        }
    }, [data]);

    return (
        <div>
            <div className={`${!toggleSearch ? 'show' : 'hide'} search-MSX-cont  `} ref={suggestionRef}>
                <TextField
                    className={`${autoSuggestHText ? 'error-feild' : ''}${showSuggestion ? 'active' : ''}`}
                    value={searchValue}
                    data-testid="search-box-test"
                    onFocus={handleFocus}
                    onChange={handleSearch}
                    placeholder="e.g: 3092603 - Contoso - 11+JID-7HH"
                    autoComplete={'off'}
                    componentRef={textInput}
                    ariaLabel="Search by MSX Opportunity ID / Account ID / Customer Name"
                />
                {showSuggestion && (
                    <div className={`suggest-cont-box`}>
                        {isLoading ? (
                            <div className="p-a-20">
                                <Shimmer className="p-b-5" shimmerElements={shimmerElement} />
                                <Shimmer className="p-b-5" shimmerElements={shimmerElement} />
                                <Shimmer shimmerElements={shimmerElement} />
                            </div>
                        ) : data?.length === 0 ? (
                            <div className="p-a-20">
                                <Text>
                                    <span tabIndex={0} aria-label="No Customer Found">
                                        No customer found.
                                    </span>
                                    <Link onClick={handleAddCustomerModal}>
                                        <div className="add-cust-link">
                                            <Trans>Add Customer</Trans>
                                        </div>
                                    </Link>
                                </Text>
                            </div>
                        ) : (
                            filterHelper(data).map((item: MsxSuggestDocument) => (
                                <div
                                    key={item?.id}
                                    tabIndex={0}
                                    onClick={() => handleSelectSuggestion(item)}
                                    className="suggestion-item  p-t-10 p-b-10"
                                    aria-label={`${item?.accountName} - Account ID: ${item?.accountNumber}`}
                                    onKeyPress={(e) => keyPress(e, () => handleSelectSuggestion(item))}
                                    data-testid="select-suggest">
                                    <Text>{`${item.accountName} - Account ID: ${item.accountNumber}`}</Text>
                                </div>
                            ))
                        )}
                        {data?.length !== 0 ? (
                            <div className="suggestion-item p-t-10 p-b-10">
                                <div className="seperator-bar-margins">
                                    <Separator />
                                </div>
                                <Text aria-label="Try adding a customer manually" tabIndex={0}>
                                    Try adding a customer manually.
                                </Text>
                                <Link onClick={handleAddCustomerModal}>
                                    <div className="add-cust-link">
                                        <Trans>Add Customer</Trans>
                                    </div>
                                </Link>
                            </div>
                        ) : (
                            <div></div>
                        )}
                    </div>
                )}
            </div>
            <div>
                <div></div>
                <div className={`${toggleSearch ? 'show' : 'hide'} `}>
                    <TextField
                        value={custName}
                        onChange={(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                            setCustName((e.target as HTMLInputElement | HTMLTextAreaElement).value);
                        }}
                        placeholder="e.g: Contoso Corporation"
                        className={` regular-text-field-search-customer ${custNameHText ? 'error-feild' : ''}`}
                        ariaLabel="Enter customer name"
                    />
                </div>
            </div>
            <div className={`${toggleSearch ? 'hide' : 'show'} info-msg`}>
                <Text>
                    Learn more about{' '}
                    <Text className="info-text" aria-label="Customer Information" tabIndex={-1}>
                        Customer information
                    </Text>{' '}
                    <TooltipHost
                        id={tooltipId}
                        calloutProps={calloutProps}
                        aria-label="Look up a customer by name of MSX Account ID"
                        content={'Look up a customer by name or MSX Account ID.'}>
                        <Image tabIndex={0} aria-describedby={tooltipId} src={InfoIcon} alt="Info" />
                    </TooltipHost>
                </Text>
            </div>
            {custNameHText && (
                <Text className="helper-txt">
                    <Trans>Please enter a customer name.</Trans>
                </Text>
            )}
        </div>
    );
};
const useSuggestionBoxDismiss = (ref: React.MutableRefObject<HTMLDivElement>, setShowSuggestion: (bool: boolean) => void) => {
    React.useEffect(() => {
        function handleClickOutside(event: MouseEvent | KeyboardEvent) {
            if (ref.current && !ref.current?.contains(event.target as Node)) {
                setShowSuggestion(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside, true);
        document.addEventListener('keypress', handleClickOutside, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keypress', handleClickOutside);
        };
    }, [setShowSuggestion, ref]);
};
